import React, { useState, useEffect, useRef } from "react";
import { NavLink, Outlet, useNavigate, Navigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import useBrand from "@/hooks/useBrand";  // Iter v34cg — brand default company (logo + name)
import { useLang } from "@/lib/i18n";
import { useChatWidgetsHidden } from "@/hooks/useChatWidgetsHidden";
import {
  LayoutDashboard, Tags, FileText, Truck, Boxes, Receipt, FileCheck2, Fuel,
  TrendingUp, Users as UsersIcon, ScrollText, Settings as SettingsIcon, LogOut, Languages, MessageCircle, MessageCircleOff, Menu, X, FileSignature, Home, Sparkles, UserCheck, Inbox, User as UserIcon, ChevronDown, CalendarCheck, ScanFace, MessageSquareText, MapPin, Wallet, AlertTriangle, Eye, LayoutGrid, Clock, ClipboardList, History, ShieldCheck
} from "lucide-react";
import { displayRole } from "@/lib/roleDisplay";
import KaryawanChatPopout from "@/components/chat/KaryawanChatPopout";
import ChatAgentPopout from "@/components/chat/ChatAgentPopout";
// Iter v34b0 — ChatLengkapPopout dinonaktifkan; fitur Chat internal admin diganti dengan Pengumuman (email broadcast).
// import ChatLengkapPopout from "@/components/chat/ChatLengkapPopout";
import AlertsBell from "@/components/AlertsBell";

// Iter v34n (#3b) — Group sidebar items by domain: Operasional / Logistik / Keuangan / HR / Sistem.
const NAV_GROUP_LABELS = {
  operasional: "Operasional",
  logistik: "Logistik & Gudang",
  keuangan: "Keuangan",
  hr: "HR & Karyawan",
  batching: "Batching Plant",
  sistem: "Sistem & Pengaturan",
};
const NAV_GROUP_ORDER = ["operasional", "logistik", "keuangan", "hr", "batching", "sistem"];

const NAV = [
  { to: "/dashboard", key: "dashboard", icon: LayoutDashboard, perm: "dashboard.view", group: "operasional" },
  { to: "/prices", key: "price_list", icon: Tags, perm: "price.view", group: "operasional" },
  // Iter v34co (#6) — Riwayat perubahan harga (audit trail per material).
  { to: "/prices/history", key: "price_history", icon: History, perm: "price.view", label: "Riwayat Harga", group: "operasional" },
  { to: "/pos", key: "purchase_order", icon: FileText, perm: "po.view", group: "operasional" },
  // Iter v34z (#2) — Vendor PO (procurement umum, beda dgn PO Vendor Batching). Letak di atas Penawaran Harga.
  { to: "/vendor-pos", key: "vendor_po", icon: FileSignature, perm: "vendor_po.view", label: "Pembuatan PO Vendor", group: "operasional" },
  // Iter v34z (#5) — Master Vendor (Procurement) — sumber autofill di form PO Vendor
  { to: "/vendors", key: "master_vendor", icon: UsersIcon, perm: "vendor_po.view", label: "Master Vendor", group: "operasional" },
  { to: "/quotations", key: "quotations", icon: FileSignature, perm: "quotation.view", group: "operasional" },
  { to: "/surat-jalan", key: "sj_recap", icon: Truck, perm: "sj.view", group: "logistik" },
  { to: "/stock", key: "stock_items", icon: Boxes, perm: "stock.view", group: "logistik" },
  { to: "/stock-log", key: "stock_log", icon: ScrollText, perm: "stocklog.view", group: "logistik" },
  { to: "/billing", key: "billing_invoice", icon: FileCheck2, perm: "invoice.create", group: "keuangan" },
  { to: "/billing-list", key: "billing_list", icon: Wallet, perm: "invoice.view", group: "keuangan" },
  { to: "/expenses", key: "expense_recap", icon: Receipt, perm: "expense.view", group: "keuangan" },
  { to: "/pnl", key: "pnl", icon: TrendingUp, perm: "report.view", group: "keuangan" },
  { to: "/plafon-klien", key: "credit_limits", icon: ShieldCheck, perm: "credit_limit.view", label: "Plafon Kredit Klien", group: "keuangan" },
  // Iter v34b1 — Rekonsiliasi Vendor (AP Aging)
  { to: "/rekonsiliasi-vendor", key: "vendor_reconciliation", icon: AlertTriangle, perm: "expense.view", label: "Rekonsiliasi Vendor", group: "keuangan" },
  // Iter v34z (#1) — Batching Plant module. Default NOT in any role except super_admin (perm-gated).
  { to: "/batching/dashboard", key: "batching_dashboard", icon: LayoutDashboard, perm: "batching.dashboard", label: "Dashboard Batching", group: "batching" },
  { to: "/batching/po", key: "batching_po", icon: FileText, perm: "batching.po.view", label: "PO Vendor (Batching)", group: "batching" },
  { to: "/batching/sj", key: "batching_sj", icon: Truck, perm: "batching.sj.view", label: "SJ Ready Mix", group: "batching" },
  { to: "/batching/material-sj", key: "batching_material_sj", icon: Truck, perm: "batching.sj.view", label: "SJ Material (Batu/Pasir)", group: "batching" },
  { to: "/batching/stock", key: "batching_stock", icon: Boxes, perm: "batching.stock.view", label: "Stok Material Batching", group: "batching" },
  { to: "/batching/solar", key: "batching_solar", icon: Fuel, perm: "batching.stock.view", label: "Pencatatan Solar", group: "batching" },
  { to: "/batching/input-material", key: "batching_input", icon: ScrollText, perm: "batching.stock.input", label: "Input Material", group: "batching" },
  { to: "/batching/rekap", key: "batching_rekap", icon: TrendingUp, perm: "batching.rekap.view", label: "Rekap Readymix", group: "batching" },
  { to: "/batching/rekap-material", key: "batching_rekap_material", icon: TrendingUp, perm: "batching.rekap.view", label: "Rekap Pengiriman Material", group: "batching" },
  // Iter v34z (#3 Phase C) — Master Batching: kelola Mix Design composition, Sopir, Truk
  { to: "/batching/masters", key: "batching_masters", icon: SettingsIcon, perm: "batching.sj.view", label: "Master Batching", group: "batching" },
];

// Client-only nav (Iter v34n #5d — Chat CS dihapus dari menu utama klien)
// Iter v34t (#4) — Sidebar grup untuk portal Klien & Karyawan.
const CLIENT_NAV = [
  { to: "/client", label: "Beranda", icon: Home, testkey: "client-home", group: "umum" },
  { to: "/client/catalog", label: "Daftar Harga", icon: Sparkles, testkey: "client-catalog", group: "katalog" },
  { to: "/client/pos", label: "PO & Kuota", icon: FileText, testkey: "client-pos", group: "transaksi" },
  { to: "/client/surat-jalan", label: "Surat Jalan", icon: Truck, testkey: "client-sj", group: "transaksi" },
  // Iter v34{af} — client sees only their own invoices.
  { to: "/client/invoices", label: "Invoice Saya", icon: Receipt, testkey: "client-invoices", group: "transaksi" },
];

// Iter v34u (#2) — Chat antar karyawan dikembalikan.
const KARYAWAN_NAV = [
  { to: "/karyawan/absen", label: "Absensi", icon: UserCheck, testkey: "karyawan-absen", group: "absensi" },
  { to: "/karyawan/cuti", label: "Cuti / Sakit", icon: CalendarCheck, testkey: "karyawan-cuti", group: "absensi" },
  { to: "/karyawan/hutang", label: "Hutang", icon: Wallet, testkey: "karyawan-hutang", needs_debt: true, group: "keuangan" },
  { to: "/employee-requests", label: "Pengajuan Fasilitas", icon: ClipboardList, testkey: "karyawan-request", perm: "employee_request.create", group: "pengajuan" },
  { to: "/karyawan/chat-teman", label: "Chat Karyawan", icon: MessageSquareText, testkey: "karyawan-chat-teman", group: "komunikasi" },
  { to: "/karyawan/chat-hr", label: "Chat HR", icon: MessageCircle, testkey: "karyawan-chat-hr", group: "komunikasi" },
];

// Iter v34t (#4) — labels for group headers shown in sidebar.
const CLIENT_GROUP_LABELS = { umum: "Umum", katalog: "Katalog", transaksi: "Transaksi" };
const KARYAWAN_GROUP_LABELS = { absensi: "Absensi & Cuti", keuangan: "Keuangan", pengajuan: "Pengajuan", komunikasi: "Komunikasi" };

const ADMIN_NAV = [
  { to: "/users", key: "users", icon: UsersIcon, group: "sistem" },
  { to: "/absensi", key: "absensi", icon: CalendarCheck, perm: "attendance.recap", label: "Rekap Absensi", group: "hr" },
  { to: "/absensi-recovery", key: "absensi-recovery", icon: AlertTriangle, perm: "attendance.delete", label: "Recovery Absensi", group: "hr" },
  { to: "/absensi/analitik", key: "absensi_analytics", icon: CalendarCheck, perm: "attendance.recap", label: "Analitik Absensi", group: "hr" },
  { to: "/absensi/rekap", key: "absensi_rekap_dept", icon: CalendarCheck, perm: "attendance.recap_dept", label: "Rekap per Departemen", group: "hr" },
  { to: "/employee-requests", key: "employee_requests", icon: ClipboardList, perm: "employee_request.approve", label: "Pengajuan Karyawan", group: "hr" },
  { to: "/cuti-admin", key: "cuti_admin", icon: CalendarCheck, perm: "leave.approve", label: "Persetujuan Cuti", group: "hr" },
  { to: "/cuti-quotas", key: "cuti_quotas", icon: SettingsIcon, perm: "employees.edit", label: "Kuota Cuti", group: "hr" },
  { to: "/libur-nasional", key: "libur_nasional", icon: CalendarCheck, role: "super_admin", label: "Libur Nasional", group: "hr" },
  { to: "/absensi/audit-wajah", key: "face_audit", icon: ScanFace, perm: "attendance.face_audit", label: "Audit Wajah", group: "hr" },
  { to: "/absensi/kiosk", key: "absensi_kiosk", icon: ScanFace, perm: "attendance.kiosk", label: "Absen Kiosk (Face)", group: "hr" },
  { to: "/karyawan/kelola-admin", key: "admin_kelola_karyawan", icon: UsersIcon, perm: "employees.admin_edit", label: "Kelola Karyawan (Admin)", group: "hr" },
  { to: "/karyawan/inbox-hr", key: "karyawan_cs_inbox", icon: MessageSquareText, perm: "hr.inbox", label: "Inbox Chat Karyawan (HR)", group: "hr" },
  // Iter v34ay — Overtime Report + Master Tarif
  { to: "/laporan-lembur", key: "overtime_report", icon: Clock, perm: "overtime.report", label: "Laporan Lembur", group: "hr" },
  { to: "/master-tarif-overtime", key: "master_tarif_overtime", icon: Wallet, perm: "overtime.report", label: "Master Tarif Overtime", group: "hr" },
  // Iter v34b2 — Payroll Otomatis
  { to: "/payroll", key: "payroll", icon: Wallet, role_any: ["super_admin"], label: "Payroll Otomatis", group: "hr" },
  // Iter v34b4 (fix #9) — Tambal Absen
  { to: "/tambal-absen", key: "tambal_absen", icon: CalendarCheck, perm: "attendance.tambal|attendance.edit", label: "Tambal Absen", group: "hr" },
  { to: "/chat", key: "chat_full", icon: MessageCircle, role_any: ["super_admin", "admin"], label: "Pengumuman", group: "sistem" },
  { to: "/admin/profile-changes", key: "profile_changes", icon: UserIcon, role: "super_admin", label: "Pengajuan Edit Profil", group: "hr" },
  { to: "/hutang-karyawan", key: "employee_debts", icon: Wallet, role_any: ["super_admin", "finance"], label: "Hutang Karyawan", group: "hr" },
  { to: "/lokasi-kantor", key: "office_locations", icon: MapPin, perm: "settings.manage", label: "Lokasi Kantor", group: "sistem" },
  { to: "/admin/pending-signups", key: "pending_signups", icon: UserCheck, group: "sistem" },
  { to: "/admin/agent-inbox", key: "agent_inbox", icon: Inbox, group: "sistem" },
  { to: "/logs", key: "logs", icon: ScrollText, group: "sistem" },
  { to: "/settings", key: "settings", icon: SettingsIcon, group: "sistem" },
];

export default function Layout() {
  const { user, logout, hasPerm } = useAuth();
  const brand = useBrand();  // Iter v34cg
  const { t, lang, toggle } = useLang();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(true);          // desktop: expanded/collapsed
  const [mobileOpen, setMobileOpen] = useState(false); // mobile: sidebar overlay open?
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);

  useEffect(() => {
    const onDocClick = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) setUserMenuOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  const isAdmin = user?.role === "super_admin";
  const realIsClient = (user?.account_type || "").toLowerCase() === "klien";
  const realIsKaryawan = (user?.account_type || "").toLowerCase() === "karyawan";
  // Iter v34ax — Portal Switcher: admin/karyawan (non-klien) dapat preview portal Klien/Karyawan.
  // State dipersist di localStorage sehingga tetap aktif saat refresh.
  // Iter v34cm/v34cn — Perm `portal.switch` untuk tombol "Ganti Portal".
  // super_admin/engineer/admin auto via wildcard `*`. Juga default-allow untuk
  // role hr sehingga tombol muncul tanpa harus set perm manual.
  // Iter v34co — Fix: hapus exclusion realIsKaryawan agar user karyawan
  // yang diberi perm `portal.switch` oleh engineer juga bisa melihat tombol.
  // Klien tetap dikecualikan (tidak boleh switch).
  const _defaultPortalRoles = new Set(["super_admin", "engineer", "admin", "hr"]);
  const canSwitchPortal = !realIsClient && (
    hasPerm("portal.switch") || _defaultPortalRoles.has((user?.role || "").toLowerCase())
  );
  const [viewAs, setViewAs] = useState(() => (canSwitchPortal ? (localStorage.getItem("view_as_portal") || "") : ""));
  const [portalMenuOpen, setPortalMenuOpen] = useState(false);
  const portalMenuRef = useRef(null);
  useEffect(() => {
    const onDocClick = (e) => {
      if (portalMenuRef.current && !portalMenuRef.current.contains(e.target)) setPortalMenuOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);
  const applyViewAs = (mode) => {
    if (!canSwitchPortal) return;
    setViewAs(mode);
    if (mode) localStorage.setItem("view_as_portal", mode);
    else localStorage.removeItem("view_as_portal");
    setPortalMenuOpen(false);
    // Navigate to landing of that portal
    if (mode === "klien") navigate("/client");
    else if (mode === "karyawan") navigate("/karyawan/absen");
    else navigate("/dashboard");
  };
  const isClient = realIsClient || (canSwitchPortal && viewAs === "klien");
  const isKaryawan = realIsKaryawan || (canSwitchPortal && viewAs === "karyawan");
  // Iter v34aq — Preload face-api models in the background as soon as a
  // karyawan account lands in the app shell. By the time they open /karyawan/absen
  // the models are already warm & ready, saving 2-5 seconds on first camera use.
  useEffect(() => {
    if (!isKaryawan) return;
    (async () => {
      try {
        const mod = await import("@/lib/faceApi");
        mod.preloadFaceApi && mod.preloadFaceApi();
      } catch { /* best-effort */ }
    })();
  }, [isKaryawan]);
  const { hidden: chatHidden, toggle: toggleChatHidden } = useChatWidgetsHidden();
  const [clock, setClock] = useState(() => new Date().toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false }));
  useEffect(() => {
    const id = setInterval(() => setClock(new Date().toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false })), 1000);
    return () => clearInterval(id);
  }, []);
  // Close mobile drawer whenever route changes
  const closeMobile = () => setMobileOpen(false);
  const visibleNav = NAV.filter((it) => {
    if (!it.perm) return true;
    if (it.perm === "dashboard.view") {
      return hasPerm("dashboard.view") || hasPerm("dashboard.finance") || hasPerm("dashboard.po") || hasPerm("dashboard.sj") || hasPerm("dashboard.notifications");
    }
    // Iter v34cl — support "|" as OR-list untuk perm, mis. "attendance.tambal|attendance.edit"
    if (typeof it.perm === "string" && it.perm.includes("|")) {
      return it.perm.split("|").some((p) => hasPerm(p.trim()));
    }
    return hasPerm(it.perm);
  });

  // CLIENT MODE: Redirect away from internal routes the client doesn't need.
  // Allowed routes for clients: /client, /client/catalog (chat is popout, not a page).
  // Engineer-set permissions can grant additional access; but by default, client lands on /client.
  if (isClient) {
    const allowed = ["/client", "/client/catalog", "/client/cart", "/client/notifications", "/client/surat-jalan", "/client/pos", "/client/invoices", "/client/chat-cs", "/profile"];
    const onAllowed = allowed.some((p) => location.pathname === p || location.pathname.startsWith(p + "/"));
    if (!onAllowed && (location.pathname === "/" || location.pathname === "/dashboard" || !visibleNav.some((it) => it.to === location.pathname))) {
      return <Navigate to="/client" replace />;
    }
  } else if (isKaryawan) {
    // Iter v34q (#3) — Wajib lengkapi profil dulu sebelum akses halaman lain.
    const profileAllowed = ["/karyawan/profil", "/karyawan/edit-profil", "/profile", "/verify-email"];
    const onProfilePage = profileAllowed.some((p) => location.pathname === p || location.pathname.startsWith(p + "/"));
    if (user?.profile_completed === false && !onProfilePage) {
      return <Navigate to="/karyawan/profil" replace />;
    }
    // Iter v28 Phase 3b — Karyawan portal gate.
    // Iter v34j (#10) — Karyawan dgn role yang punya perms ekstra dapat akses
    // halaman operasional sesuai perm. Cek apakah path target diizinkan oleh perm role.
    // Iter v34k (#2) — Hutang/Kasbon hanya bisa diakses jika allow_debt=true.
    const baseAllowed = ["/karyawan", "/karyawan/absen", "/karyawan/cuti",
                         "/karyawan/chat-hr", "/karyawan/chat-teman",
                         "/karyawan/ringkasan", "/karyawan/profil", "/karyawan/edit-profil",
                         "/profile"];
    const debtPaths = ["/karyawan/hutang", "/karyawan/kasbon"];
    const allowed = user?.allow_debt ? [...baseAllowed, ...debtPaths] : baseAllowed;
    const onAllowed = allowed.some((p) => location.pathname === p || location.pathname.startsWith(p + "/"));
    // Block hutang/kasbon explicitly if allow_debt=false
    if (!user?.allow_debt && debtPaths.some((p) => location.pathname.startsWith(p))) {
      return <Navigate to="/karyawan/absen" replace />;
    }
    // Cek apakah karyawan punya perm untuk akses path operasional via role
    const targetNav = NAV.find((it) => it.to === location.pathname);
    const hasRolePermForPath = targetNav && hasPerm(targetNav.perm);
    const adminNav = ADMIN_NAV.find((it) => it.to === location.pathname);
    const hasAdminPermForPath = adminNav && (
      (adminNav.perm && hasPerm(adminNav.perm)) ||
      (adminNav.role_any && adminNav.role_any.includes(user?.role))
    );
    // Iter v34cw (#4) — Karyawan dengan perm dari role juga boleh membuka menu
    // portal karyawan yang ber-perm (mis. /employee-requests dgn employee_request.create).
    // Sebelumnya hanya NAV & ADMIN_NAV yang dicek sehingga menu Pengajuan Fasilitas selalu ditolak.
    const karyawanNav = KARYAWAN_NAV.find((it) => it.to === location.pathname);
    const hasKaryawanPermForPath = karyawanNav && (!karyawanNav.perm || hasPerm(karyawanNav.perm));
    if (!onAllowed && !hasRolePermForPath && !hasAdminPermForPath && !hasKaryawanPermForPath) {
      return <Navigate to="/karyawan/absen" replace />;
    }
  }
  // Original "if (!onAllowed) ..." block previously closed here.
  if (false) {
      return <Navigate to="/client" replace />;
    }

  // Iter v34cf — Kiosk mode: render halaman tanpa sidebar/topbar untuk fullscreen.
  if (location.pathname === "/absensi/kiosk") {
    return <Outlet />;
  }


  return (
    <div className="min-h-screen flex bg-paper">
      {/* Mobile overlay backdrop */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 bg-stone-900/50 md:hidden" onClick={closeMobile} data-testid="mobile-sidebar-backdrop" />
      )}
      {/* Sidebar — drawer on mobile (fixed + transform), inline on desktop (md+) */}
      <aside
        className={`
          fixed inset-y-0 left-0 z-50 ${mobileOpen ? "translate-x-0" : "-translate-x-full"} md:sticky md:top-0 md:translate-x-0
          ${open ? "w-64" : "w-16"} md:h-screen h-full
          transition-all duration-200 shrink-0 border-r border-stone-200/70 bg-white/95 backdrop-blur-sm flex flex-col shadow-sm
        `}
        data-testid="sidebar"
      >
        <div className="h-16 flex items-center gap-3 px-4 border-b border-stone-200/70">
          {/* Iter v34cg — Logo perusahaan dari default company (fallback ke inisial R). */}
          <div className={`h-9 w-9 shrink-0 rounded-xl flex items-center justify-center text-white font-bold shadow-md overflow-hidden ${isClient ? "bg-gradient-to-br from-blue-500 to-emerald-500" : "bg-gradient-to-br from-blue-600 to-emerald-500"}`} data-testid="sidebar-brand-logo">
            {brand.logoUrl ? (
              <img src={brand.logoUrl} alt="Logo" className="w-full h-full object-contain bg-white" />
            ) : (
              <span>R</span>
            )}
          </div>
          {open && (
            <div className="flex-1 min-w-0">
              <p className="font-display font-semibold text-stone-900 leading-none tracking-tight" data-testid="sidebar-brand-name">{isClient ? "Portal Klien" : (brand.name || "RBB ERP")}</p>
              <p className="text-[10px] uppercase tracking-widest text-stone-500 mt-1 truncate">{isClient ? (user?.client_name || "Klien") : "Material · Konstruksi"}</p>
            </div>
          )}
          <button onClick={closeMobile} className="md:hidden text-stone-500 hover:text-stone-900" data-testid="mobile-sidebar-close">
            <X className="h-5 w-5" />
          </button>
        </div>
        {/* Iter v34br (Task 7) — Batasi tinggi nav ~ 15 item (44px each = 660px + padding).
            Item ke-16+ akan scroll di dalam nav container ini. */}
        <nav className="flex-1 overflow-y-auto py-4 px-2 space-y-0.5 max-h-[calc(15*2.75rem+2rem)]" onClick={() => mobileOpen && closeMobile()}>
          {isClient ? (
            <>
              {/* Iter v34t (#4) — group klien sidebar by `group` field */}
              {(() => {
                const grouped = {};
                CLIENT_NAV.forEach((it) => {
                  const g = it.group || "umum";
                  grouped[g] = grouped[g] || [];
                  grouped[g].push(it);
                });
                const order = ["umum", "katalog", "transaksi"];
                return order.filter((k) => grouped[k]).map((gkey) => (
                  <div key={gkey} className="pb-1">
                    {open && (
                      <div className="px-4 py-1.5 text-[10px] uppercase tracking-[0.2em] text-stone-400 mt-3 font-semibold">{CLIENT_GROUP_LABELS[gkey] || gkey}</div>
                    )}
                    {grouped[gkey].map((it) => (
                      <NavLink key={it.to} to={it.to} end
                        className={({ isActive }) =>
                          `flex items-center gap-2.5 px-3 py-2 mx-0.5 text-sm rounded-lg transition-all ${
                            isActive ? "bg-stone-900 text-white font-medium shadow-sm" : "text-stone-600 hover:bg-stone-100 hover:text-stone-900"
                          }`
                        }
                        data-testid={`nav-${it.testkey}`}
                      >
                        <it.icon className="h-4 w-4 shrink-0" />
                        {open && <span>{it.label}</span>}
                      </NavLink>
                    ))}
                  </div>
                ));
              })()}
              {open && (
                <div className="px-4 mt-6 pt-4 border-t border-stone-100">
                  <p className="text-[10px] uppercase tracking-widest text-stone-400 mb-2">Bantuan</p>
                  <p className="text-[11px] text-stone-500 leading-relaxed">
                    Butuh bantuan? Klik tombol <span className="font-semibold text-emerald-700">Chat CS</span> di pojok kanan bawah untuk terhubung langsung dengan tim Customer Service kami.
                  </p>
                </div>
              )}
            </>
          ) : isKaryawan ? (
            <>
              {/* Iter v34t (#4) — group karyawan sidebar */}
              {(() => {
                const items = KARYAWAN_NAV.filter((it) => (!it.needs_debt || user?.allow_debt) && (!it.perm || hasPerm(it.perm)));
                const grouped = {};
                items.forEach((it) => {
                  const g = it.group || "akun";
                  grouped[g] = grouped[g] || [];
                  grouped[g].push(it);
                });
                // Iter v34cw (#4) — grup "pengajuan" sebelumnya tidak ada di urutan render
                // sehingga menu "Pengajuan Fasilitas" tidak pernah tampil untuk karyawan.
                const order = ["absensi", "keuangan", "pengajuan", "komunikasi", "akun"];
                return order.filter((k) => grouped[k]).map((gkey) => (
                  <div key={gkey} className="pb-1">
                    {open && (
                      <div className="px-4 py-1.5 text-[10px] uppercase tracking-[0.2em] text-stone-400 mt-3 font-semibold">{KARYAWAN_GROUP_LABELS[gkey] || gkey}</div>
                    )}
                    {grouped[gkey].map((it) => (
                      <NavLink key={it.to} to={it.to} end
                        className={({ isActive }) =>
                          `flex items-center gap-2.5 px-3 py-2 mx-0.5 text-sm rounded-lg transition-all ${
                            isActive ? "bg-stone-900 text-white font-medium shadow-sm" : "text-stone-600 hover:bg-stone-100 hover:text-stone-900"
                          }`
                        }
                        data-testid={`nav-${it.testkey}`}
                      >
                        <it.icon className="h-4 w-4 shrink-0" />
                        {open && <span>{it.label}</span>}
                      </NavLink>
                    ))}
                  </div>
                ));
              })()}
              {/* Iter v34j (#10) — Karyawan dgn role tambahan dapat akses menu operasional / admin sesuai perm. */}
              {(() => {
                const operExtra = NAV.filter((it) => it.perm && hasPerm(it.perm));
                const adminExtra = ADMIN_NAV.filter((it) => {
                  if (it.perm && hasPerm(it.perm)) return true;
                  if (it.role_any && it.role_any.includes(user?.role)) return true;
                  if (it.role && user?.role === it.role) return true;
                  return false;
                });
                const extras = [...operExtra, ...adminExtra];
                if (extras.length === 0) return null;
                return (
                  <>
                    <div className={`px-4 py-1.5 mt-4 text-[10px] uppercase tracking-[0.2em] text-stone-400 font-semibold ${!open && "hidden"}`}>Akses Role</div>
                    {extras.map((it) => (
                      <NavLink key={`role-${it.to}`} to={it.to}
                        className={({ isActive }) =>
                          `flex items-center gap-2.5 px-3 py-2 mx-0.5 text-sm rounded-lg transition-all ${
                            isActive ? "bg-stone-900 text-white font-medium shadow-sm" : "text-stone-600 hover:bg-stone-100 hover:text-stone-900"
                          }`
                        }
                        data-testid={`nav-karyawan-role-${it.key || it.testkey}`}
                      >
                        <it.icon className="h-4 w-4 shrink-0" />
                        {open && <span>{it.label || t(it.key || "")}</span>}
                      </NavLink>
                    ))}
                  </>
                );
              })()}
            </>
          ) : (
            <>
              {/* Iter v34n (#3b) — Group sidebar items by domain (Operasional, Logistik, Keuangan, HR, Sistem). */}
              {/* Iter v34r (#4+#5) — BUGFIX: ADMIN_NAV sebelumnya hanya tampil untuk super_admin
                  karena gate `isAdmin ?`. Akibatnya: user yang diberi role kustom dengan perm baru
                  (mis. attendance.recap_dept, leave.approve) TIDAK BISA melihat menunya. Sekarang
                  filter berbasis perm — sama persis seperti yang diatur engineer di Role UI. */}
              {(() => {
                const filteredAdmin = ADMIN_NAV.filter((it) => {
                  if (it.perm) return hasPerm(it.perm);
                  if (it.role_any) return it.role_any.includes(user?.role);
                  if (it.role) return user?.role === it.role;
                  return isAdmin; // only super_admin sees unrestricted entries
                });
                const allItems = [...visibleNav, ...filteredAdmin];
                const byGroup = {};
                allItems.forEach((it) => {
                  const g = it.group || "sistem";
                  (byGroup[g] = byGroup[g] || []).push(it);
                });
                const renderLink = (it) => (
                  <NavLink key={it.to} to={it.to} end
                    className={({ isActive }) =>
                      `flex items-center gap-2.5 px-3 py-2 mx-0.5 text-sm rounded-lg transition-all ${
                        isActive ? "bg-stone-900 text-white font-medium shadow-sm" : "text-stone-600 hover:bg-stone-100 hover:text-stone-900"
                      }`
                    }
                    data-testid={`nav-${it.key}`}
                  >
                    <it.icon className="h-4 w-4 shrink-0" />
                    {open && <span>{it.label || t(it.key)}</span>}
                  </NavLink>
                );
                return NAV_GROUP_ORDER.map((g) => {
                  const items = byGroup[g] || [];
                  if (items.length === 0) return null;
                  return (
                    <React.Fragment key={g}>
                      <div className={`px-4 py-1.5 mt-3 text-[10px] uppercase tracking-[0.2em] text-stone-400 font-semibold ${!open && "hidden"}`}>
                        {NAV_GROUP_LABELS[g]}
                      </div>
                      {items.map(renderLink)}
                    </React.Fragment>
                  );
                });
              })()}
            </>
          )}
        </nav>
        <button onClick={() => setOpen((v) => !v)} className="h-10 border-t border-stone-200/70 text-[10px] uppercase tracking-widest text-stone-500 hover:text-stone-900 hover:bg-stone-50 transition-colors" data-testid="sidebar-toggle">
          {open ? "« Collapse" : "»"}
        </button>
      </aside>

      {/* Main */}
      <div className="flex-1 min-w-0 flex flex-col">
        {/* Topbar */}
        <header className="h-16 border-b border-stone-200/70 bg-white/80 backdrop-blur-md flex items-center justify-between px-3 sm:px-4 md:px-6 gap-2 sticky top-0 z-30">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            {/* Iter v34m (#5) — Desktop sidebar toggle (always-visible hamburger). */}
            <button
              onClick={() => setOpen((v) => !v)}
              className="hidden md:inline-flex items-center justify-center w-9 h-9 rounded-full text-stone-700 hover:text-stone-900 hover:bg-stone-100 transition-colors"
              title={open ? "Sembunyikan sidebar" : "Tampilkan sidebar"}
              data-testid="desktop-sidebar-toggle"
            >
              <Menu className="h-5 w-5" />
            </button>
            {/* Mobile hamburger */}
            <button onClick={() => setMobileOpen(true)} className="md:hidden w-9 h-9 rounded-full flex items-center justify-center text-stone-700 hover:text-stone-900 hover:bg-stone-100" data-testid="mobile-menu-btn">
              <Menu className="h-5 w-5" />
            </button>
            {!isClient && !isKaryawan && (
              <span className="hidden lg:inline text-[10px] uppercase tracking-[0.25em] text-stone-400 font-semibold">/ Workspace</span>
            )}
            <span className="hidden md:inline font-mono text-xs text-stone-600 truncate">{new Date().toLocaleDateString("id-ID", { weekday: "long", day: "2-digit", month: "long", year: "numeric" })}</span>
            <span className="font-mono text-xs sm:text-sm font-semibold text-stone-900 md:border-l md:border-stone-300 md:pl-3 tracking-wider tabular-nums" data-testid="clock-24h">{clock}</span>
          </div>
          <div className="flex items-center gap-2 sm:gap-3 shrink-0">
            {/* Iter v34ax — Portal Switcher: admin dapat preview portal Klien/Karyawan */}
            {canSwitchPortal && (
              <div className="relative" ref={portalMenuRef}>
                <button
                  onClick={() => setPortalMenuOpen((v) => !v)}
                  className={`flex items-center gap-1.5 text-[10px] sm:text-xs uppercase tracking-widest border rounded-full px-2 sm:px-3 py-1.5 transition-colors ${
                    viewAs
                      ? "bg-blue-50 border-blue-300 text-blue-700 hover:bg-blue-100"
                      : "bg-white border-stone-200 text-stone-500 hover:text-stone-900 hover:border-stone-400"
                  }`}
                  data-testid="portal-switcher-btn"
                  title="Ganti tampilan portal"
                >
                  {viewAs ? <Eye className="h-3.5 w-3.5" /> : <LayoutGrid className="h-3.5 w-3.5" />}
                  <span className="hidden sm:inline">
                    {viewAs === "klien" ? "Preview: Klien" : viewAs === "karyawan" ? "Preview: Karyawan" : "Portal"}
                  </span>
                </button>
                {portalMenuOpen && (
                  <div
                    className="absolute right-0 top-full mt-2 w-56 bg-white border border-stone-200/70 shadow-xl rounded-2xl z-50 py-1 overflow-hidden"
                    data-testid="portal-switcher-menu"
                  >
                    <div className="px-4 py-2 border-b border-stone-100 bg-stone-50/50">
                      <p className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">Ganti Portal</p>
                    </div>
                    <button
                      onClick={() => applyViewAs("")}
                      className={`w-full flex items-center gap-2 px-4 py-2 text-sm hover:bg-stone-50 ${!viewAs ? "font-semibold text-stone-900" : "text-stone-700"}`}
                      data-testid="portal-switch-admin"
                    >
                      <LayoutDashboard className="h-4 w-4 text-stone-500" />
                      <span>Portal Admin</span>
                      {!viewAs && <span className="ml-auto text-[9px] uppercase tracking-widest text-emerald-600">Aktif</span>}
                    </button>
                    <button
                      onClick={() => applyViewAs("klien")}
                      className={`w-full flex items-center gap-2 px-4 py-2 text-sm hover:bg-stone-50 ${viewAs === "klien" ? "font-semibold text-stone-900" : "text-stone-700"}`}
                      data-testid="portal-switch-klien"
                    >
                      <Home className="h-4 w-4 text-stone-500" />
                      <span>Portal Klien</span>
                      {viewAs === "klien" && <span className="ml-auto text-[9px] uppercase tracking-widest text-emerald-600">Aktif</span>}
                    </button>
                    <button
                      onClick={() => applyViewAs("karyawan")}
                      className={`w-full flex items-center gap-2 px-4 py-2 text-sm hover:bg-stone-50 ${viewAs === "karyawan" ? "font-semibold text-stone-900" : "text-stone-700"}`}
                      data-testid="portal-switch-karyawan"
                    >
                      <UserCheck className="h-4 w-4 text-stone-500" />
                      <span>Portal Karyawan</span>
                      {viewAs === "karyawan" && <span className="ml-auto text-[9px] uppercase tracking-widest text-emerald-600">Aktif</span>}
                    </button>
                  </div>
                )}
              </div>
            )}
            {/* Two bells: Notifications (info) + Warnings (critical)
                Iter v34d (#6) — Karyawan portal sees only the notification bell.
                Warning bell carries SJ/stock/billing alerts that don't apply to them. */}
            <div className="flex items-center gap-0.5 sm:gap-1 border border-stone-200 rounded-full px-1 sm:px-1.5 py-0.5 bg-white">
              <AlertsBell variant="notification" />
              {!isKaryawan && (
                <>
                  <span className="text-stone-300">|</span>
                  <AlertsBell variant="warning" />
                </>
              )}
            </div>
            <button onClick={toggle} className="hidden sm:flex items-center gap-1.5 text-[10px] sm:text-xs uppercase tracking-widest text-stone-500 hover:text-stone-900 hover:border-stone-400 border border-stone-200 rounded-full px-2 sm:px-3 py-1.5 transition-colors" data-testid="lang-toggle">
              <Languages className="h-3.5 w-3.5" />{lang === "id" ? "EN" : "ID"}
            </button>
            <div className="flex items-center gap-2 sm:gap-3 pl-2 sm:pl-3 sm:border-l border-stone-200 relative" ref={userMenuRef}>
              <button
                type="button"
                onClick={() => setUserMenuOpen((v) => !v)}
                className="flex items-center gap-2 sm:gap-3 hover:opacity-90 transition-opacity"
                data-testid="user-menu-trigger"
                aria-haspopup="menu"
                aria-expanded={userMenuOpen}
              >
                <div className="text-right hidden sm:block">
                  <p className="text-sm font-medium text-stone-900 leading-none truncate max-w-[120px]" data-testid="user-name">{user?.name || "—"}</p>
                  <p className="text-[10px] uppercase tracking-widest text-stone-500 mt-1" data-testid="user-role">{isClient ? "Klien" : displayRole(user?.role)}</p>
                </div>
                <div className={`h-9 w-9 rounded-full ${isClient ? "bg-gradient-to-br from-blue-500 to-emerald-500" : "bg-gradient-to-br from-stone-900 to-stone-700"} text-white flex items-center justify-center text-xs font-display font-bold shrink-0 overflow-hidden ring-2 ring-white shadow-md`}>
                  {user?.photo_b64 ? (
                    <img src={`data:image/png;base64,${user.photo_b64}`} alt="" className="h-full w-full object-cover" />
                  ) : (
                    (user?.name || "U").slice(0, 1).toUpperCase()
                  )}
                </div>
                <ChevronDown className={`h-3.5 w-3.5 text-stone-500 transition-transform ${userMenuOpen ? "rotate-180" : ""}`} />
              </button>
              {userMenuOpen && (
                <div
                  className="absolute right-0 top-full mt-2 w-56 bg-white border border-stone-200/70 shadow-xl rounded-2xl z-50 py-1 overflow-hidden"
                  role="menu"
                  data-testid="user-menu-dropdown"
                >
                  <div className="px-4 py-3 border-b border-stone-100 bg-stone-50/50">
                    <p className="text-xs font-medium text-stone-900 truncate">{user?.name || "—"}</p>
                    <p className="text-[10px] text-stone-500 truncate">{user?.email || ""}</p>
                  </div>
                  <Link
                    to={isKaryawan ? "/karyawan/edit-profil" : "/profile"}
                    onClick={() => setUserMenuOpen(false)}
                    className="flex items-center gap-2 px-4 py-2 text-sm text-stone-700 hover:bg-stone-50"
                    data-testid="user-menu-profile"
                  >
                    <UserIcon className="h-4 w-4 text-stone-500" />
                    <span>{isKaryawan ? "Edit Profil Saya" : "Edit Profil"}</span>
                  </Link>
                  <button
                    onClick={() => { toggleChatHidden(); setUserMenuOpen(false); }}
                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-stone-700 hover:bg-stone-50"
                    data-testid="user-menu-toggle-chat"
                    title="Tampilkan / sembunyikan chat popup mengambang"
                  >
                    {chatHidden ? <MessageCircle className="h-4 w-4 text-stone-500" /> : <MessageCircleOff className="h-4 w-4 text-stone-500" />}
                    <span>{chatHidden ? "Tampilkan Chat Popup" : "Sembunyikan Chat Popup"}</span>
                  </button>
                  <button
                    onClick={() => { setUserMenuOpen(false); doLogout(); }}
                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50 border-t border-stone-100"
                    data-testid="user-menu-logout"
                  >
                    <LogOut className="h-4 w-4" />
                    <span>{t("logout")}</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto">
          {viewAs && canSwitchPortal && (
            <div className="bg-blue-50 border-b border-blue-200 px-4 md:px-6 py-2 flex items-center justify-between gap-3" data-testid="preview-mode-banner">
              <div className="flex items-center gap-2 text-xs text-blue-800 min-w-0">
                <Eye className="h-3.5 w-3.5 shrink-0" />
                <span className="truncate">
                  Mode Preview — Anda melihat <b>Portal {viewAs === "klien" ? "Klien" : "Karyawan"}</b> sebagai admin.
                </span>
              </div>
              <button
                onClick={() => applyViewAs("")}
                className="text-[10px] uppercase tracking-widest text-blue-700 hover:text-blue-900 font-semibold border border-blue-300 rounded-full px-3 py-1 hover:bg-white transition"
                data-testid="preview-exit-btn"
              >
                Keluar Preview
              </button>
            </div>
          )}
          <Outlet />
        </main>
      </div>

      {/* Iter v34v (#5) — Chat popouts. Iter v34b0: ChatLengkapPopout dinonaktifkan (buggy + text terbalik di mobile — user request diganti dengan fitur Pengumuman via email). */}
      {!chatHidden && <ChatAgentPopout />}
      {!chatHidden && <KaryawanChatPopout />}
    </div>
  );
}
